import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { containerOf, noteDraft, noteDraftChanged, noteMoveColumns } from '@cerebro-diem/core';
import type { FilingTarget, Note, NoteDraft } from '@cerebro-diem/core';
import { supabase } from '../../services/supabase';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { useContainers } from '../../services/containers';
import { updateNote } from '../../services/notes';
import ContainerChip, { chipClass } from '../../components/ContainerChip';
import ContainerPicker from '../../components/ContainerPicker';
import DeleteItem from '../../components/DeleteItem';
import DictationTextarea from '../../components/DictationTextarea';
import { inputClass, outlineButton, primaryButton } from '../../components/styles';
import { useUnsavedChanges } from '../../components/useUnsavedChanges';

/** One note: edit it, move it, delete it, and see the people it involves. */
export default function NoteDetailPage() {
  const { id = '' } = useParams();
  const noteQuery = useQuery({
    queryKey: queryKeys.note(id),
    queryFn: async () => {
      const { data, error } = await supabase.from('notes').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return (data ?? null) as Note | null;
    },
  });
  if (noteQuery.isError) return <p className="text-sm text-error">Could not load this note: {errorMessage(noteQuery.error)}</p>;
  if (noteQuery.isPending) return <p className="text-sm text-on-surface-variant">Loading…</p>;
  if (!noteQuery.data) return <p className="text-on-surface-variant">This note was not found.</p>;
  return <NoteEditor key={noteQuery.data.id} note={noteQuery.data} />;
}

function NoteEditor({ note }: { note: Note }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const containersQuery = useContainers();
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body ?? '');
  const current = containerOf(note);
  const [target, setTarget] = useState<FilingTarget | null>(current);
  const [validation, setValidation] = useState<string | null>(null);
  // What is saved: the note as loaded, then each edit once Save succeeds (the page stays open).
  const [saved, setSaved] = useState<NoteDraft>(() => noteDraft(note));
  // Leaving with an unsaved edit asks first (the Done link, any other link, closing the tab).
  const { leaveWithoutAsking } = useUnsavedChanges(noteDraftChanged(saved, { title, body, target }));

  const peopleQuery = useQuery({
    queryKey: [...queryKeys.note(note.id), 'people'],
    queryFn: async () => {
      const { data: links, error } = await supabase.from('note_people').select('person_id').eq('note_id', note.id);
      if (error) throw error;
      const ids = (links ?? []).map((link: { person_id: string }) => link.person_id);
      if (ids.length === 0) return [];
      const { data, error: peopleError } = await supabase.from('people').select('id, name').in('id', ids).order('name');
      if (peopleError) throw peopleError;
      return (data ?? []) as { id: string; name: string }[];
    },
  });

  const mutation = useMutation({
    mutationFn: (draft: NoteDraft) => {
      const to = draft.target;
      if (!to || 'newName' in to) throw new Error('choose where this note lives');
      const moved = to.kind !== current?.kind || to.id !== current?.id;
      return updateNote(note.id, { title: draft.title.trim(), body: draft.body.trim() || null, ...(moved ? noteMoveColumns(to) : {}) });
    },
    onSuccess: (_updated, draft) => {
      setSaved(draft);
      return Promise.all([queryKeys.notes, queryKeys.containers, queryKeys.searches].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });

  const containers = containersQuery.data ?? [];
  // Says what the picker chooses; the chosen container's kind is the icon beside it.
  const placement = 'Project, area or resource';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setValidation('A note needs a title.');
      return;
    }
    if (!target || 'newName' in target) {
      setValidation('Choose where this note lives.');
      return;
    }
    setValidation(null);
    mutation.mutate({ title, body, target });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-on-surface-variant">
        <span>Note in</span>
        <ContainerChip item={note} containersById={new Map(containers.map((c) => [c.id, c]))} />
      </div>

      <form onSubmit={submit} className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-on-surface-variant">Title</span>
          <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <DictationTextarea label="Body" rows={10} value={body} onChange={setBody} />
        <label className="block space-y-1">
          <span className="text-xs font-medium text-on-surface-variant">{placement}</span>
          <ContainerPicker containers={containers} kinds={['project', 'area', 'resource']} value={target} onChange={setTarget} label={placement} />
        </label>

        {validation && <p className="text-sm text-error">{validation}</p>}
        {mutation.isError && <p className="text-sm text-error">Not saved: {errorMessage(mutation.error)}</p>}
        {mutation.isSuccess && <p className="text-sm text-success">Saved.</p>}

        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" disabled={mutation.isPending} className={primaryButton}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
          <Link to="/" className={outlineButton}>
            Done
          </Link>
          {/* The note is gone, so its edits are too: nothing to ask about. */}
          <DeleteItem kind="note" id={note.id} name={note.title} onDeleted={() => leaveWithoutAsking(() => navigate('/'))} />
        </div>
      </form>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-on-surface-variant">People</h2>
        {peopleQuery.isError ? (
          <p className="text-sm text-error">Could not load people: {errorMessage(peopleQuery.error)}</p>
        ) : peopleQuery.isPending ? (
          <p className="text-sm text-on-surface-variant">Loading…</p>
        ) : peopleQuery.data.length === 0 ? (
          <p className="text-sm text-on-surface-variant">No people linked.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {peopleQuery.data.map((person) => (
              <span key={person.id} className={`${chipClass} bg-primary-container text-primary`}>
                {person.name}
              </span>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
